import { MoneyDisplay } from '../common/MoneyDisplay';
import { HeroProgressBar } from '../common/HeroProgressBar';
import pt from '../../locales/pt';
import { formatEUR } from '../../utils/money';
import { cn } from '../../utils/cn';
import { groupHasSpendingHistory } from './categoryBudgetSuggestionUtils';

/**
 * Escala visual: alvo do método (ou max limite/média).
 */
export function buildGroupRefBarMetrics(group) {
  const defined = Number(group?.categories_budget_sum_eur ?? group?.budget_eur ?? 0);
  const target = Number(group?.target_budget_eur ?? 0);
  const average =
    group?.average_spent_eur != null ? Number(group.average_spent_eur) : null;
  const hasHistory = groupHasSpendingHistory(group);

  const scale = (() => {
    if (Number.isFinite(target) && target > 0) return target;
    const candidates = [defined];
    if (hasHistory && average != null) candidates.push(average);
    const max = Math.max(...candidates.filter((n) => Number.isFinite(n) && n > 0));
    return max > 0 ? max : 1;
  })();

  const clampPct = (value) => {
    if (!Number.isFinite(value) || value <= 0) return 0;
    return Math.min(100, (value / scale) * 100);
  };

  return {
    defined: Number.isFinite(defined) ? defined : 0,
    target: Number.isFinite(target) && target > 0 ? target : null,
    average: hasHistory && average != null ? average : null,
    hasHistory,
    scale,
    fillPct: clampPct(defined),
    methodPct: clampPct(target > 0 ? target : scale),
    averagePct: hasHistory && average != null ? clampPct(average) : null,
  };
}

/**
 * Camada 2 — barra de limite: preenchimento simples (sem marcas de
 * referência) + "Limite {X}". A média (quando existe) só é mostrada por
 * categoria (`CategoryBudgetSliderRow`), não ao nível do grupo — `.pen` →
 * `nmlc6` (grupos), `labels` mostra só o limite.
 */
export function BudgetGroupRefBar({ group, hasIncome, className }) {
  if (!hasIncome) return null;

  const metrics = buildGroupRefBarMetrics(group);
  const color = group.color || '#5F7E54';

  return (
    <div className={cn('px-space-4 pb-space-3', className)}>
      <HeroProgressBar
        className="mt-space-1"
        data-testid="budget-group-ref-bar"
        percentage={metrics.fillPct}
        fillColor={color}
        trackColor={`${color}22`}
        height={12}
        radius="rounded-ds-sm"
        ariaLabel={group.name}
      />

      <div className="mt-space-2">
        <span className="type-chip font-semibold text-sage-on-tint">
          {pt.budget.groupRefs.limit.replace('{amount}', formatEUR(metrics.defined))}
        </span>
      </div>
    </div>
  );
}

/**
 * Legenda partilhada das 3 referências.
 */
export function BudgetGroupRefLegend({ className }) {
  return (
    <div
      className={cn(
        'type-chip flex flex-wrap items-center justify-center gap-x-space-4 gap-y-1 pt-space-1 font-normal text-muted-foreground',
        className,
      )}
      data-testid="budget-group-ref-legend"
    >
      <span className="inline-flex items-center gap-space-2">
        <span className="h-2 w-3 rounded-sm bg-sage-600" aria-hidden />
        {pt.budget.groupRefs.legendLimit}
      </span>
      <span className="inline-flex items-center gap-space-2">
        <span className="h-2.5 w-0.5 bg-foreground" aria-hidden />
        {pt.budget.groupRefs.legendMethod}
      </span>
      <span className="inline-flex items-center gap-space-2">
        <span className="h-2.5 w-0.5 bg-muted-foreground" aria-hidden />
        {pt.budget.groupRefs.legendAverage}
      </span>
    </div>
  );
}

/** Compact first-cycle group summary (alvo + média a chegar). */
export function BudgetGroupFirstCycleRow({ group, className }) {
  const defined = Number(group.categories_budget_sum_eur ?? group.budget_eur ?? 0);
  const targetPct =
    group.target_pct != null ? Number(group.target_pct).toFixed(0) : null;
  const color = group.color || '#5F7E54';

  return (
    <div
      className={cn(
        'flex items-center gap-2 rounded-[14px] border border-border border-l-4 bg-card px-3.5 py-3',
        className,
      )}
      style={{ borderLeftColor: color }}
    >
      <span className="min-w-0 flex-1 text-[13.5px] font-bold text-foreground">
        {group.name}
      </span>
      <div className="shrink-0 text-right">
        <MoneyDisplay
          value={defined}
          className="font-display text-lg font-bold tabular-nums"
        />
        <p className="text-[10px] text-muted-foreground">
          {targetPct != null
            ? pt.budget.groupRefs.targetPctAverageComing.replace('{pct}', targetPct)
            : pt.budget.groupRefs.averageComing}
        </p>
      </div>
    </div>
  );
}
